import classnames from 'classnames';
import { Data, LearningCenter, UI, Utils } from 'common';
import { DeliveryConfirmationOptions } from 'common/src/code/data/deliveryConfirmation';
import DeliveryConfirmation from 'common/src/code/data/deliveryConfirmation/DeliveryConfirmation.enum';
import { WrappedButton } from 'common/src/code/ui/button';
import Typography from 'common/src/code/ui/Typography.component';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import isNull from 'lodash/isNull';
import kebabCase from 'lodash/kebabCase';
import moment from 'moment';
import { Accordion, AccordionTab } from 'primereact/accordion';
import React from 'react';
import {
	CostDetails as CostDetailsEnum,
	FeeType,
} from '../shared/CostDetails.enum';
import {
	carrierTrackingName,
	isCarrierTrackingSpecialCase,
} from '../shared/DetailsDataHelper';
import FeeDetails from '../shared/FeeDetails';
import FeesPopoverContent from '../shared/FeesPopoverContent';
import FeeUtil from '../shared/FeeUtil';
import { TrackingNumberType } from '../shared/TrackingNumberType.enum';
import HistoryUtil from '../utils/Util';
import { DataDisplayPanel } from './DataDisplayPanel.component';
import { PackagingType as PackagingTypeEnum } from './PackagingType.enum';

const { Util, Session } = Utils;
const { Carrier, Countries } = Data;
const { WrappedTooltip, WrappedPopover, WrappedButtonType } = UI;
const { LearningCenterArticleLink } = LearningCenter;

const extraServiceFeeCodes = {
	...CostDetailsEnum[FeeType.EXTRA_SERVICE],
	LIVE_ANIMALS: CostDetailsEnum[FeeType.OTHER].LIVE_ANIMALS,
	LIVE_ANIMALS_WITH_FEE: CostDetailsEnum[FeeType.OTHER].LIVE_ANIMALS_WITH_FEE,
};

const isUspsTrackingExtraServiceSpecialCase = ({
	carrier,
	deliveryConfirmationType,
}) =>
	carrier === Carrier.USPS &&
	(deliveryConfirmationType === DeliveryConfirmation.AdultSignature ||
		deliveryConfirmationType === DeliveryConfirmation.DirectSignature);

const trackingDetail = (
	carrier,
	carrierName,
	deliveryConfirmationType,
	deliveryConfirmationAmount,
	deliveryConfirmationTypeName,
	trackingNumber,
	trackingNumberType
) => {
	// Same conditions being applied in grid for determining whether to show tracking # or tracking confirmation type
	if (!HistoryUtil.showTracking(trackingNumber, trackingNumberType)) {
		return false;
	} else if (
		isCarrierTrackingSpecialCase({ deliveryConfirmationAmount, carrier })
	) {
		return carrierTrackingName({
			carrier,
			carrierName,
			deliveryConfirmationAmount,
		});
	} else if (
		isUspsTrackingExtraServiceSpecialCase({ carrier, deliveryConfirmationType })
	) {
		// USPS Adult Signature and Direct Signature are displayed under extra service in EXT, not tracking.
		return false;
	} else {
		return deliveryConfirmationAmount ? deliveryConfirmationTypeName : '';
	}
};

const trackingAmount = ({
	carrier,
	deliveryConfirmationType,
	deliveryConfirmationAmount,
}) => {
	if (
		!isCarrierTrackingSpecialCase({ deliveryConfirmationAmount, carrier }) &&
		!isUspsTrackingExtraServiceSpecialCase({
			carrier,
			deliveryConfirmationType,
		})
	) {
		return Util.formatCurrency(deliveryConfirmationAmount);
	}

	return null;
};

const dataDisplayExtraServices = (
	carrier,
	deliveryConfirmationType,
	trackingNumber,
	deliveryConfirmationAmount,
	surchargeDetails,
	costDetails,
	labelDetails
) => {
	const { filteredCostDetails } = FeeUtil.filterByFeeCodesAndCalculateTotal({
		costDetails,
		feeCodes: extraServiceFeeCodes,
	});
	const feeDetails = FeeUtil.feeDetailsMap({ feeDetails: filteredCostDetails });
	const {
		nonMachinable,
		saturdayDelivery,
		certifiedMail,
		returnReceipt,
		returnReceiptElectronic,
		sundayOrHolidayDelivery,
		restrictedDelivery,
		noticeOfNonDelivery,
		isPayOnUse,
		registeredMail,
		collectOnDelivery,
		specialHandling,
		noLabel,
		uspsNonstandardPackageCharacteristics,
		liveAnimals,
		liveAnimalsWithFee,
	} = feeDetails || {};

	// search for static data
	const confirmationOption =
		find(DeliveryConfirmationOptions, ['value', deliveryConfirmationType]) ||
		{};

	const { label: confirmationOptionLabel, tooltip: confirmationOptionTooltip } =
		confirmationOption;

	const extraServicesDetails = [
		{
			// special case for usps tracking/extra service
			dependsOn: isUspsTrackingExtraServiceSpecialCase({
				carrier,
				deliveryConfirmationType,
			}),
			label: {
				text: confirmationOptionLabel,
				tooltip: confirmationOptionTooltip,
				tooltipOptions: {
					position: 'bottom',
				},
			},
			amount: {
				value: Util.formatCurrency(deliveryConfirmationAmount),
				dataqa: `${kebabCase(confirmationOptionLabel?.feeCode)}-amount`,
			},
		},
		{
			dependsOn: certifiedMail,
			label: {
				text: certifiedMail?.feeName,
			},
			amount: {
				value: Util.formatCurrency(certifiedMail?.cost ?? 0),
				dataqa: `${kebabCase(certifiedMail?.feeCode)}-amount`,
			},
		},
		{
			dependsOn: registeredMail,
			label: {
				text: `Registered Mail® ($${Util.formatCurrency(registeredMail?.value ?? 0)})`,
			},
			amount: {
				value: Util.formatCurrency(registeredMail?.cost ?? 0),
				dataqa: `${kebabCase(registeredMail?.feeCode)}-amount`,
			},
		},
		{
			dependsOn: returnReceipt,
			label: {
				text: returnReceipt?.feeName,
			},
			amount: {
				value: Util.formatCurrency(returnReceipt?.cost ?? 0),
				dataqa: `${kebabCase(returnReceipt?.feeCode)}-amount`,
			},
		},
		{
			dependsOn: collectOnDelivery,
			label: {
				text: collectOnDelivery?.feeName,
			},
			amount: {
				value: Util.formatCurrency(collectOnDelivery?.cost ?? 0),
				dataqa: `${kebabCase(collectOnDelivery?.feeCode)}-amount`,
			},
		},
		{
			dependsOn: noticeOfNonDelivery,
			label: {
				text: noticeOfNonDelivery?.feeName,
			},
			amount: {
				value: Util.formatCurrency(noticeOfNonDelivery?.cost ?? 0),
				dataqa: `${kebabCase(noticeOfNonDelivery?.feeCode)}-amount`,
			},
		},
		{
			dependsOn: restrictedDelivery,
			label: {
				text: restrictedDelivery?.feeName,
			},
			amount: {
				value: Util.formatCurrency(restrictedDelivery?.cost ?? 0),
				dataqa: `${kebabCase(restrictedDelivery?.feeCode)}-amount`,
			},
		},
		{
			dependsOn: sundayOrHolidayDelivery,
			label: {
				text: sundayOrHolidayDelivery?.feeName,
			},
			amount: {
				value: Util.formatCurrency(sundayOrHolidayDelivery?.cost ?? 0),
				dataqa: `${kebabCase(sundayOrHolidayDelivery?.feeCode)}-amount`,
			},
		},
		{
			dependsOn: nonMachinable,
			label: {
				text: nonMachinable?.feeName,
			},
			amount: {
				value: Util.formatCurrency(nonMachinable?.cost ?? 0),
				dataqa: `${kebabCase(nonMachinable?.feeCode)}-amount`,
			},
		},
		{
			dependsOn: uspsNonstandardPackageCharacteristics,
			label: {
				text: `${uspsNonstandardPackageCharacteristics?.feeName} ${labelDetails?.isRectangularShaped === true ? '(Rectangular)' : '(Non-Rectangular)'}`,
				isLongServiceName: true,
			},
			amount: {
				value: Util.formatCurrency(
					uspsNonstandardPackageCharacteristics?.cost ?? 0
				),
				dataqa: `${kebabCase(uspsNonstandardPackageCharacteristics?.feeCode)}-amount`,
			},
		},
		{
			dependsOn: saturdayDelivery,
			label: {
				text: saturdayDelivery?.feeName,
			},
			amount: {
				value: Util.formatCurrency(saturdayDelivery?.cost ?? 0),
				dataqa: `${kebabCase(saturdayDelivery?.feeCode)}-amount`,
			},
		},
		{
			dependsOn: returnReceiptElectronic,
			label: {
				text: returnReceiptElectronic?.feeName,
				key: 'electronic_return_receipt',
				tooltip:
					'<b>Electronic Return Receipt</b><br>' +
					'<p>Your copy of the Electronic Return Receipt signature can be retrieved in two easy steps:</p>' +
					'<ol>' +
					'<li>When viewing the tracking details for your label on the USPS.com Track & Confirm page, click on the Return Receipt Electronic link under the Features column.</li>' +
					'<li>Enter your name and email address to have a copy of the Electronic Return Receipt signature emailed to you.</li>' +
					'</ol>',
				tooltipOptions: {
					position: 'bottom',
				},
			},
			amount: {
				value: Util.formatCurrency(returnReceiptElectronic?.cost ?? 0),
				dataqa: `${kebabCase(returnReceiptElectronic?.feeCode)}-amount`,
			},
			id: 'electronic-return-receipt',
		},
		{
			dependsOn: isPayOnUse,
			label: {
				text: isPayOnUse?.feeName,
			},
			amount: {
				value: Util.formatCurrency(isPayOnUse?.cost ?? 0),
				dataqa: `${kebabCase(isPayOnUse?.feeCode)}-amount`,
			},
		},
		{
			dependsOn: specialHandling,
			label: {
				text: specialHandling?.feeName,
			},
			amount: {
				value: Util.formatCurrency(specialHandling?.cost ?? 0),
				dataqa: `${kebabCase(specialHandling?.feeCode)}-amount`,
			},
		},
		{
			dependsOn: noLabel,
			label: {
				text: noLabel?.feeName,
			},
			amount: {
				value: Util.formatCurrency(noLabel?.cost ?? 0),
				dataqa: `${kebabCase(noLabel?.feeCode)}-amount`,
			},
		},
		{
			dependsOn: liveAnimals,
			label: {
				text: liveAnimals?.feeName,
			},
			amount: {
				value: Util.formatCurrency(liveAnimals?.cost ?? 0),
				dataqa: `${kebabCase(liveAnimals?.feeCode)}-amount`,
			},
		},
		{
			dependsOn: liveAnimalsWithFee,
			label: {
				text: liveAnimalsWithFee?.feeName,
			},
			amount: {
				value: Util.formatCurrency(liveAnimalsWithFee?.cost ?? 0),
				dataqa: `${kebabCase(liveAnimalsWithFee?.feeCode)}-amount`,
			},
		},
	].filter(({ dependsOn }) => dependsOn);

	const extraServices = (
		<div>
			<div>
				{extraServicesDetails.length > 0 && (
					<div>
						{extraServicesDetails.map(({ label, amount }, targetIndex) => {
							const { text, key, tooltip, tooltipOptions, isLongServiceName } =
								label;
							const { value, dataqa: amountDataQA } = amount || {};
							const labelTooltipTarget = `extra-service-${targetIndex}`;

							return (
								<div key={targetIndex} className="itemized-price-group">
									<React.Fragment key={`${text}-${targetIndex}`}>
										{trackingNumber && key === 'electronic_return_receipt' ? (
											<a
												key={`link-${targetIndex}`}
												target="_blank"
												className={classnames(
													labelTooltipTarget,
													'service-dot-spacer',
													{ 'long-service-name': isLongServiceName }
												)}
											>
												{text}
											</a>
										) : (
											<span
												key={`label-${targetIndex}`}
												className={classnames(
													labelTooltipTarget,
													'service-dot-spacer',
													{ 'long-service-name': isLongServiceName }
												)}
											>
												{text}
											</span>
										)}
										<WrappedTooltip
											tooltip={tooltip}
											target={labelTooltipTarget}
											{...tooltipOptions}
										/>
										<span
											key={`amount-${targetIndex}`}
											className="p-col-1 display-amount"
											data-qa={amountDataQA}
											{...{ dataqa: amountDataQA }}
										>
											{value}
										</span>
									</React.Fragment>
								</div>
							);
						})}
					</div>
				)}
			</div>
			<div>
				{surchargeDetails?.length > 0 && (
					<div>
						{surchargeDetails.map(
							(
								{ surchargeCode, surchargeDescription, surchargeAmount },
								targetIndex
							) => (
								<div
									key={`${surchargeCode}-${targetIndex}`}
									className="itemized-price-group"
								>
									<span
										key={`label-${surchargeCode}-${targetIndex}`}
										className="service-dot-spacer display-white-space"
									>
										{surchargeDescription}
									</span>

									<span
										key={`amount-${surchargeCode}-${targetIndex}`}
										className="p-col-1 display-amount"
										data-qa={`${surchargeCode}-'amount'`}
										{...{ dataqa: `${surchargeCode}-'amount'` }}
									>
										{Util.formatCurrency(surchargeAmount)}
									</span>
								</div>
							)
						)}
					</div>
				)}
			</div>
		</div>
	);

	return extraServices;
};

const DataDisplayInsurance = ({
	labelDetails,
	insuranceType,
	features,
	onFileClaimClick,
}) => {
	const { insuranceOptions } = labelDetails || {};
	const { insuredValue, amount: insuranceCost } = insuranceOptions || {};
	const { amount } = insuredValue || {};
	const insuranceName =
		insuranceType === 'parcelguard'
			? 'ParcelGuard&trade;'
			: 'Stamps.com Insurance';
	const hasClaimButton = features.visible && amount > 0;

	const insuranceTemplate = (
		<div
			className={`data-display-insurance ${hasClaimButton ? 'has-claim-button' : ''}`}
		>
			<div>{Util.formatCurrency(amount)}</div>
			<div className="p-col-8 display-body">
				{features.visible && amount > 0 && (
					<>
						<WrappedButton
							label="File Claim"
							className="file-claims-button"
							dataqa="common-buttongrid-btn"
							buttonType={WrappedButtonType.TertiaryGrey}
							icon={null}
							disabled={features.disabled}
							onClick={onFileClaimClick}
						/>
						<WrappedTooltip
							tooltip={`<b>File Claim</b><br />File a ${insuranceName} claim for this print.`}
							target="file-claims-button"
							position="bottom"
						/>
					</>
				)}
			</div>
			<div className="display-amount">{Util.formatCurrency(insuranceCost)}</div>
		</div>
	);

	return amount > 0 ? insuranceTemplate : 'None';
};

const getAdjustmentDetail = adjustDetail => {
	if (adjustDetail.label === 'Adjustment Amount:') {
		return Util.formatCurrency(parseFloat(adjustDetail.body));
	} else if (adjustDetail.label === 'Processing Fee:') {
		return (
			<div className="detail-inline-items">
				{Util.formatCurrency(parseFloat(adjustDetail.body))}{' '}
				<LearningCenterArticleLink
					linkText="Learn more"
					stamps="20821909092123-Post-Shipment-Billing-Adjustments#UUID-44bf062a-2549-fe26-ea92-13a06504e77c_UUID-a2d7d0f1-d3f7-84cf-cdb5-8c5740d52e7d"
				/>
			</div>
		);
	} else {
		return adjustDetail.body;
	}
};

const AccordionCarrierServicesData = ({ labelDetails }) => {
	const { adjustedAmount, adjustmentDetails = [] } = labelDetails;
	const {
		date,
		reason = '',
		serviceTypeName = '',
		packagingTypeName = '',
		weightPounds,
		weightOunces,
		adjustmentId,
		toCountry,
		adjustmentProcessingFee,
	} = adjustmentDetails[0] || {};

	const { countryName = '' } = isEmpty(toCountry)
		? ''
		: Countries.find(({ countryCode }) => countryCode === toCountry);

	const adjustmentAmountWithoutFee = Util.formatCurrency(
		(adjustedAmount || 0) - (adjustmentProcessingFee || 0)
	);

	const weight =
		`${weightPounds ? `${weightPounds} lbs.` : ''} ${weightOunces ? `${weightOunces} oz.` : ''}`.trim();

	const adjustDataDisplay = [
		{
			body: date ? moment(date).format('MM/DD/YYYY') : '',
			dataqa: 'adj-date',
			label: 'Adj. Date:',
		},
		{
			body: reason,
			dataqa: 'adj-reason',
			label: 'Adj. Reason:',
		},
		{
			body: serviceTypeName,
			dataqa: 'adj-service',
			label: 'Adj. Service:',
		},
		{
			body: packagingTypeName,
			dataqa: 'adj-packaging',
			label: 'Adj. Packaging:',
		},
		{
			body: weight,
			dataqa: 'adj-weight',
			label: 'Adj. Weight:',
		},
		{
			body: adjustmentId,
			dataqa: 'adj-id',
			label: 'Adj. ID:',
		},
		{
			body: countryName,
			dataqa: 'adj-to-country',
			label: 'Adj. To Country:',
		},
		{
			body: adjustmentAmountWithoutFee,
			dataqa: 'adj-amount',
			label: 'Adj. Amount:',
		},
		{
			body: adjustmentProcessingFee,
			dataqa: 'adj-processing-fee',
			label: 'Processing Fee:',
		},
	];

	return (
		<div>
			{adjustDataDisplay.map(
				(adjustDetail, i) =>
					adjustDetail.body &&
					!(
						adjustDetail.label === 'Processing Fee:' &&
						parseFloat(adjustDetail.body) === 0
					) && (
						<DataDisplayPanel
							label={adjustDetail.label}
							body={getAdjustmentDetail(adjustDetail)}
							dataqa={adjustDetail.dataqa}
							key={`adjustment-detail-${i}`}
							serviceDataDisplay
						/>
					)
			)}
		</div>
	);
};

const AccordionServicesData = ({
	printDetails,
	labelDetails,
	features,
	onFileClaimClick,
	showUpgradeModal,
}) => {
	const {
		carrierName = '',
		serviceTypeName = '',
		printTypeName = '',
		weightOunces = '',
		weightPounds = '',
		paymentTypeName = '',
		deliveryConfirmationType = '',
		deliveryConfirmationTypeName = '',
		deliveryConfirmationAmount = '',
		carrier = '',
		insuranceType = '',
		netstampCount,
		isReturnLabel = false,
		trackingNumberType,
	} = printDetails || {};
	const {
		trackingNumber,
		isCubicPricing,
		serviceAmount = 0,
		surchargeDetails = [],
		package: packageDetails = {},
		shipmentCost = {},
	} = labelDetails || {};
	const {
		height = '',
		length = '',
		width = '',
		packagingTypeName = '',
		packagingType = '',
	} = packageDetails || {};

	const { costDetails = [] } = shipmentCost;

	const { filteredCostDetails } = FeeUtil.filterByFeeCodesAndCalculateTotal({
		costDetails,
		feeCodes: [CostDetailsEnum.OTHER.DELIVERED_DUTY_PAID],
	});
	const feeDetails = FeeUtil.feeDetailsMap({ feeDetails: filteredCostDetails });
	const { deliveredDutyPaid } = feeDetails || {};

	const serviceDetails = [
		{
			body: printTypeName === 'NetStamps' ? netstampCount : '',
			dataqa: 'stamp-qty',
			label: 'Stamp Qty:',
		},
		{
			body: carrierName,
			dataqa: 'carrier',
			label: 'Carrier:',
		},
		{
			body: HistoryUtil.formattedServiceTypeName(
				isReturnLabel,
				carrier,
				serviceTypeName
			),
			dataqa: 'service',
			label: 'Service:',
			amount: isNull(serviceAmount) ? 0 : serviceAmount,
		},
		{
			body: PackagingTypeEnum[packagingType] || packagingTypeName,
			dataqa: 'Packaging',
			label: 'Packaging:',
		},
		{
			body: (
				<DataDisplayInsurance
					labelDetails={labelDetails}
					carrier={carrier}
					insuranceType={insuranceType}
					features={features}
					onFileClaimClick={onFileClaimClick}
				/>
			),
			containerClassName: 'data-display-insurance-container',
			dataqa: 'insurance',
			label: carrier === Carrier.UPS ? 'Declared Value:' : 'Insurance:',
		},
		{
			body: trackingDetail(
				carrier,
				carrierName,
				deliveryConfirmationType,
				deliveryConfirmationAmount,
				deliveryConfirmationTypeName,
				trackingNumber,
				trackingNumberType
			),
			dataqa: 'tracking',
			label: 'Tracking:',
			amount: trackingAmount({
				carrier,
				deliveryConfirmationType,
				deliveryConfirmationAmount,
			}),
		},
		{
			body:
				FeeUtil.hasFeeCodes({
					costDetails,
					feeCodes: extraServiceFeeCodes,
				}) &&
				dataDisplayExtraServices(
					carrier,
					deliveryConfirmationType,
					trackingNumber,
					deliveryConfirmationAmount,
					surchargeDetails,
					costDetails,
					labelDetails
				),
			dataQA: 'extra-services',
			label: 'Extra Services:',
		},
		{
			body:
				FeeUtil.hasFeeCodes({
					costDetails,
					feeCodes: [CostDetailsEnum.OTHER.DELIVERED_DUTY_PAID],
				}) && deliveredDutyPaid?.feeName,
			dataQA: 'delivered-duty-paid',
			label: 'Duties/Taxes:',
			amount: Util.formatCurrency(deliveredDutyPaid?.cost ?? 0),
		},
		{
			body: `${weightPounds} lbs. ${weightOunces} oz.`,
			dataqa: 'weight',
			label: 'Weight:',
		},
		{
			body: isCubicPricing ? Number(isCubicPricing) : '',
			dataqa: 'cubic-rate',
			label: 'Cubic Rate:',
		},
		{
			body: height > 0 && `${length}" x ${width}" x ${height}"`,
			dataqa: 'dimensions',
			label: 'Dimensions:',
		},
		{
			body: paymentTypeName,
			dataqa: 'payment-type',
			label: 'Payment Type:',
		},
		{
			body: FeeUtil.hasFeeCodes({
				costDetails,
				feeCodes: CostDetailsEnum[FeeType.PRINT],
			}) && <FeeDetails costDetails={costDetails} />,
			dataQA: 'fees',
			label: (
				<div className="fee-items-container">
					<Typography.P>Fees:</Typography.P>
					<WrappedPopover triggerElement={<div className="fee-items-icon" />}>
						<FeesPopoverContent onUpgradeClick={showUpgradeModal} />
					</WrappedPopover>
				</div>
			),
		},
	];

	return (
		<div>
			{serviceDetails.map(
				(serviceDetail, i) =>
					serviceDetail.body && (
						<DataDisplayPanel
							label={serviceDetail.label}
							body={serviceDetail.body}
							dataqa={serviceDetail.dataqa}
							key={`${serviceDetail.dataqa}_${i}`}
							amount={serviceDetail.amount}
							containerClassName={serviceDetail.containerClassName}
							serviceDataDisplay
						/>
					)
			)}
		</div>
	);
};

const AccordionDisplay = ({
	labelDetails,
	printDetails,
	features,
	onFileClaimClick,
	showUpgradeModal,
}) => {
	const { amount = '', estimatedAmount = '', adjustedAmount } = labelDetails;
	const { refundApprovedAmount = '' } = printDetails;

	return amount !== 0 ? (
		<Accordion
			multiple
			activeIndex={[0]}
			dataqa="print-services-amount-paid-collapse-button"
		>
			<AccordionTab
				header={
					<DataDisplayPanel
						label="Amount Paid"
						body={`${Util.formatCurrency(amount)}`}
						dataqa="amount-paid"
						serviceHeaderData
						serviceDataDisplay
					/>
				}
			>
				<AccordionServicesData
					labelDetails={labelDetails}
					printDetails={printDetails}
					features={features}
					onFileClaimClick={onFileClaimClick}
					showUpgradeModal={showUpgradeModal}
				/>
			</AccordionTab>

			{adjustedAmount && (
				<AccordionTab
					header={
						<DataDisplayPanel
							label="Adjustment Applied"
							body={`${Util.formatCurrency(adjustedAmount)}`}
							dataqa="carrier-adjustment"
							serviceHeaderData
							serviceDataDisplay
						/>
					}
				>
					<AccordionCarrierServicesData labelDetails={labelDetails} />
				</AccordionTab>
			)}
			<AccordionTab
				header={
					<DataDisplayPanel
						label="Quoted Amount"
						body={`${Util.formatCurrency(estimatedAmount)}`}
						dataqa="quoted-amount"
						serviceHeaderData
						serviceDataDisplay
					/>
				}
			>
				<AccordionServicesData
					labelDetails={labelDetails}
					printDetails={printDetails}
					features={features}
					onFileClaimClick={onFileClaimClick}
					showUpgradeModal={showUpgradeModal}
				/>
			</AccordionTab>
		</Accordion>
	) : (
		<>
			<div className="no-data">
				<DataDisplayPanel
					label="Amount Paid"
					body={`${Util.formatCurrency(amount)}`}
					dataqa="amount-paid"
					serviceHeaderData
					serviceDataDisplay
				/>
			</div>
			{refundApprovedAmount && (
				<div className="no-data">
					<DataDisplayPanel
						label="Refunded Amount:"
						body={`${Util.formatCurrency(refundApprovedAmount)}`}
						dataqa="refunded-amount"
						serviceHeaderData
						serviceDataDisplay
					/>
				</div>
			)}
			<Accordion
				multiple
				activeIndex={[0]}
				dataqa="print-services-amount-quoted-collapse-button"
			>
				{adjustedAmount && (
					<AccordionTab
						header={
							<DataDisplayPanel
								label="Adjustment Applied"
								body={`${Util.formatCurrency(adjustedAmount)}`}
								dataqa="carrier-adjustment"
								serviceHeaderData
								serviceDataDisplay
							/>
						}
					>
						<AccordionCarrierServicesData labelDetails={labelDetails} />
					</AccordionTab>
				)}
				<AccordionTab
					header={
						<DataDisplayPanel
							label="Quoted Amount"
							body={`${Util.formatCurrency(estimatedAmount)}`}
							dataqa="quoted-amount"
							serviceHeaderData
							serviceDataDisplay
						/>
					}
				>
					<AccordionServicesData
						labelDetails={labelDetails}
						printDetails={printDetails}
						features={features}
						onFileClaimClick={onFileClaimClick}
						showUpgradeModal={showUpgradeModal}
					/>
				</AccordionTab>
			</Accordion>
		</>
	);
};

const ServicesPanel = ({
	labelAndOrderDetails,
	printDetails,
	features,
	onFileClaimClick,
	labelDetail,
	showUpgradeModal,
}) => {
	const { labelDetails = {} } = labelAndOrderDetails;

	return (
		<AccordionDisplay
			labelDetails={labelDetails}
			printDetails={printDetails}
			features={features}
			onFileClaimClick={onFileClaimClick}
			labelDetail={labelDetail}
			showUpgradeModal={showUpgradeModal}
		/>
	);
};

export default ServicesPanel;
